import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Lens } from "@hookform/lenses"
import { cn } from "@/lib/utils"
import { BetterDocSection } from "@/modules/patients/detail/better-doc/components/better-doc-section"
import { CareProviderContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/care-provider-content"
import { DoctorVisitAvoidedContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/doctor-visit-avoided-content"
import { PurposePointOfCareSelection } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/purpose-point-of-care-selection"
import { ReasonForChoosingType } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/reason-for-choosing-type"
import { TimeToTreatContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/time-to-treat-content"
import { InsurerNetworkBanner } from "@/modules/patients/detail/progress-note/point-of-care/components/insurer-network-banner"
import { PartnerPortalBanner } from "@/modules/patients/detail/progress-note/point-of-care/components/partner-portal-banner"
import { useAppointmentOrRegisteredPatientAddress } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useAppointmentOrRegisteredPatientAddress"
import { useCheckPartnerPortalEligibility } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useCheckPartnerPortalEligibility"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type {
  ContentProps,
  HasCareProvider,
  Purpose,
} from "@/modules/patients/detail/progress-note/point-of-care/types"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import type { MedgateServiceInfo } from "@/modules/patients/services/usePatientByIdQuery"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type GeneralPractitionerContentProps = ContentProps &
  HasCareProvider & {
    pointOfCareFormLens: Lens<PointOfCareFormType>
    medgateServiceInfo: MedgateServiceInfo
    patientId: string
    episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
    setCareProvider: (careProvider: string | null) => void
    onMedicalPointOfCareSelect: (value: PointOfCareType) => void
    onActualPointOfCareSelect: (value: PointOfCareType | undefined) => void
  }

export const GeneralPractitionerContent = (props: GeneralPractitionerContentProps) => {
  const {
    pointOfCareFormLens,
    medgateServiceInfo,
    isActual,
    countryCode,
    timeToTreatData,
    hasCareProvider,
    hasMPNReferral,
    patientId,
    episodeOfCareProgressNoteData,
    setCareProvider,
    onMedicalPointOfCareSelect,
    onActualPointOfCareSelect,
  } = props
  const { t } = useTranslation()
  const { getValues } = useFormContext<PointOfCareFormType>()
  const timeToTreatLens = pointOfCareFormLens.focus("timeToTreatType")
  const purposeLens = pointOfCareFormLens.focus("purpose")
  const timeToTreatType = useWatch(timeToTreatLens.interop())

  const isSwitzerlandCountry = countryCode === "CH"

  const patientAddress = useAppointmentOrRegisteredPatientAddress({
    patientId,
    countryCode,
    episodeOfCareProgressNoteData,
  })
  const { isEligibleToPartnerPortal } = medgateServiceInfo

  const { isEligible, isLoading, closestCareProvider } = useCheckPartnerPortalEligibility({
    patientId,
    address: patientAddress,
    enabled: isSwitzerlandCountry && isEligibleToPartnerPortal,
  })

  const handleRefer = () => {
    if (isActual) {
      const currentMedical = getValues("medicalPointOfCare")
      if (currentMedical === "TELECARE") {
        onActualPointOfCareSelect(undefined)
      } else {
        onActualPointOfCareSelect("TELECARE")
      }
    } else {
      onMedicalPointOfCareSelect("TELECARE")
    }
  }

  const { control: purposeControl, name: purposeName } = purposeLens.interop()

  return (
    <div className="col-span-2 gap-4">
      {isSwitzerlandCountry && medgateServiceInfo.hasInsuranceListReferralToGeneralPractitioner && (
        <InsurerNetworkBanner />
      )}
      {isSwitzerlandCountry && isEligible && (
        <PartnerPortalBanner onRefer={handleRefer} isLoading={isLoading} closestCareProvider={closestCareProvider} />
      )}
      {isActual && (
        <ReasonForChoosingType reasonForChoosingTypeLens={pointOfCareFormLens.focus("reasonForChoosingType")} />
      )}
      <BetterDocSection />
      <div className={cn("flex flex-col gap-4 pt-4 @lg/patient-layout:flex-row")}>
        <TimeToTreatContent
          timeToTreatLens={timeToTreatLens}
          timeToTreatData={timeToTreatData}
          appInsightsKey="time_to_treat_selection"
        />
        <div className="@lg/patient-layout:w-2/3">
          {isSwitzerlandCountry && (
            <CareProviderContent
              pointOfCareFormLens={pointOfCareFormLens}
              searchType="GENERAL_PRACTITIONER"
              hasCareProvider={hasCareProvider}
              hasMPNReferral={hasMPNReferral}
              showInsurerNetwork
              patientAddress={patientAddress}
              timeToTreat={timeToTreatType ?? undefined}
              setCareProvider={setCareProvider}
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
              usePreset
              allowUnknownCareProvider
            />
          )}
        </div>
      </div>
      <Controller
        name={purposeName}
        control={purposeControl}
        render={({ field }) => {
          const selectedPurposes: Purpose[] = field.value ?? []
          return (
            <PurposePointOfCareSelection
              label={t("episodeOfCare.progressNote.pointOfCare.purpose")}
              onChange={(purposes: Purpose[]): void => field.onChange(purposes)}
              value={selectedPurposes}
              isChecked={(purpose) => selectedPurposes.includes(purpose)}
            />
          )
        }}
      />
      <DoctorVisitAvoidedContent isDoctorVisitAvoidedLens={pointOfCareFormLens.focus("isDoctorVisitAvoided")} />
    </div>
  )
}
